import { useCallback } from "react";
import { trpc } from "../../../lib/api/trpc";
import { logError } from "../../../utils/errors/errorHandler";
import { useAuth } from "../../../hooks";

/**
 * Hook for MFA management operations
 *
 * Provides methods for enabling/disabling MFA
 */

interface MFAResult {
  success: boolean;
  error?: string;
  data?: any;
}

export const useMFA = () => {
  const { refreshUser } = useAuth();

  // Get the trpc context for revalidating queries
  // @ts-ignore - Type checking for trpc.useContext
  const utils = trpc.useContext();

  // Enable MFA mutation
  // @ts-ignore - Type checking for trpc.auth property
  const {
    mutateAsync: enableMfaAsync,
    isLoading: isEnabling,
  } = trpc.auth.enableMfa.useMutation({
    onSuccess: async () => {
      // Invalidate the auth me query to refresh user data
      // @ts-ignore - Type checking for utils.auth property
      await utils.auth.me.invalidate();
      // Also refresh user in auth context
      await refreshUser();
    },
  });

  // Disable MFA mutation
  // @ts-ignore - Type checking for trpc.auth property
  const {
    mutateAsync: disableMfaAsync,
    isLoading: isDisabling,
  } = trpc.auth.disableMfa.useMutation({
    onSuccess: async () => {
      // Invalidate the auth me query to refresh user data
      // @ts-ignore - Type checking for utils.auth property
      await utils.auth.me.invalidate();
      // Also refresh user in auth context
      await refreshUser();
    },
  });

  // Function to enable MFA
  const enableMFA = useCallback(
    async (mfaType: string = "EMAIL"): Promise<MFAResult> => {
      try {
        const result = await enableMfaAsync({ mfaType });
        return { success: true, data: result };
      } catch (err: any) {
        logError(err, "Enable MFA");
        return {
          success: false,
          error: err?.message || "Failed to enable MFA",
        };
      }
    },
    [enableMfaAsync],
  );

  // Function to disable MFA
  const disableMFA = useCallback(
    async (): Promise<MFAResult> => {
      try {
        const result = await disableMfaAsync();
        return { success: true, data: result };
      } catch (err: any) {
        logError(err, "Disable MFA");
        return {
          success: false,
          error: err?.message || "Failed to disable MFA",
        };
      }
    },
    [disableMfaAsync],
  );

  // Function to toggle MFA based on current state
  const toggleMFA = useCallback(
    async (enabled: boolean, mfaType: string = "EMAIL"): Promise<MFAResult> => {
      if (enabled) {
        return enableMFA(mfaType);
      } else {
        return disableMFA();
      }
    },
    [enableMFA, disableMFA],
  );

  return {
    enableMFA,
    disableMFA,
    toggleMFA,
    isLoading: isEnabling || isDisabling,
    isEnabling,
    isDisabling,
  };
};
